import React, { useEffect, useMemo, useState } from "react";
import { XCircle, Download, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import { api, exportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDate } from "@/lib/format";
import { Card, Loading, PageHeader, Button, Badge, EmptyState } from "@/components/common";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const ROLES = [["", "Semua"], ["CASHIER", "Kasir"], ["FLORIST", "Perangkai"], ["SUPERVISOR", "Manager"], ["OWNER", "Owner"]];
const ROLE_LABEL = { CASHIER: "Kasir", FLORIST: "Perangkai", SUPERVISOR: "Manager", OWNER: "Owner", WAREHOUSE: "Gudang", QC: "QC", DELIVERY: "Kurir", FINANCE: "Keuangan" };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shiftDay = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return iso(d); };
const fmtLong = (s) => { const d = new Date(s + "T00:00:00"); return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };

export default function VoidReport() {
  const { branch } = useAuth();
  const [scope, setScope] = useState("branch");
  const [mode, setMode] = useState("day");
  const [day, setDay] = useState(iso(new Date()));
  const [ym, setYm] = useState({ y: new Date().getFullYear(), m: new Date().getMonth() });
  const [role, setRole] = useState("");
  const [userId, setUserId] = useState("");
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const params = useMemo(() => {
    const p = { branch_id: scope === "branch" ? branch?.id : undefined, role: role || undefined, user_id: userId || undefined };
    if (mode === "day") { p.date_from = day; p.date_to = day; }
    else { p.date_from = iso(new Date(ym.y, ym.m, 1)); p.date_to = iso(new Date(ym.y, ym.m + 1, 0)); }
    return p;
  }, [scope, branch?.id, mode, day, ym, role, userId]);

  useEffect(() => { setData(null); api.get("/reports/voids", params).then(setData).catch(() => setData({ rows: [], summary: { count: 0, amount: 0, items: 0 }, by_user: [], by_role: [] })); }, [params]);

  const label = mode === "day" ? fmtLong(day) : `${MONTHS[ym.m]} ${ym.y}`;
  const prev = () => mode === "day" ? setDay((d) => shiftDay(d, -1)) : setYm(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 });
  const next = () => mode === "day" ? setDay((d) => shiftDay(d, 1)) : setYm(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 });

  return (
    <>
      <PageHeader title="Transaksi Batal / Void" subtitle="Harian & bulanan · tanggal pembatalan" icon={XCircle}
        actions={<Button variant="outline" onClick={() => window.open(exportUrl("voids", params), "_blank")} data-testid="void-download"><Download size={16} /> Excel</Button>} />

      <div className="flex flex-wrap gap-2 mb-3">
        {[["branch", branch?.name || "Cabang ini"], ["all", "Semua Cabang"]].map(([k, l]) => <button key={k} onClick={() => setScope(k)} className={`px-3 h-9 rounded-full text-sm font-medium ${scope === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
        {[["day", "Harian"], ["month", "Bulanan"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} className={`px-3 h-9 rounded-full text-sm font-medium ${mode === k ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
      </div>
      <Card className="p-3 mb-3">
        <div className="flex items-center justify-between">
          <button onClick={prev} data-testid="void-prev" className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><ChevronLeft size={18} /></button>
          <span className="font-semibold text-slate-800" data-testid="void-period-label">{label}</span>
          <button onClick={next} data-testid="void-next" className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><ChevronRight size={18} /></button>
        </div>
      </Card>
      <div className="mb-3">
        <div className="text-xs font-medium text-slate-500 mb-1">Dibatalkan oleh (role)</div>
        <div className="flex flex-wrap gap-2">
          {ROLES.map(([k, l]) => <button key={k} onClick={() => { setRole(k); setUserId(""); }} data-testid={`void-role-${k || "all"}`} className={`px-3 h-8 rounded-full text-xs font-medium ${role === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
        </div>
      </div>

      {!data ? <Loading /> : (
        <>
          <Card className="p-4 mb-4" data-testid="void-summary">
            <div className="grid grid-cols-3 gap-4">
              <Info label="Transaksi dibatalkan" value={`${data.summary.count}`} />
              <Info label="Nilai dibatalkan" value={rupiah(data.summary.amount)} />
              <Info label="Item dibatalkan" value={`${data.summary.items}`} />
            </div>
            {data.by_role?.length > 0 && <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">{data.by_role.map((r) => <span key={r.role}>oleh {ROLE_LABEL[r.role] || r.role}: {r.count} · {rupiah(r.amount)}</span>)}</div>}
          </Card>

          <h3 className="font-semibold text-slate-700 mb-2">Daftar Void ({data.rows.length})</h3>
          {data.rows.length === 0 && <EmptyState icon={XCircle} title="Tidak ada void" message="Tidak ada transaksi dibatalkan pada periode / filter ini." />}
          <div className="space-y-2">
            {data.rows.map((r) => {
              const open = openId === r.id;
              return (
                <Card key={r.id} className="p-4 cursor-pointer" onClick={() => setOpenId(open ? null : r.id)} data-testid={`void-row-${r.order_no}`}>
                  <div className="flex justify-between items-center"><span className="font-bold text-slate-800">{r.order_no}</span><Badge className="bg-rose-100 text-rose-700">{ROLE_LABEL[r.cancelled_by_role] || r.cancelled_by_role}</Badge></div>
                  <div className="text-xs text-slate-400 mt-0.5">{r.branch_name}{r.customer_name ? ` · ${r.customer_name}` : ""}{r.channel ? ` · ${r.channel}` : ""}</div>
                  <div className="flex flex-wrap gap-6 mt-2 text-sm">
                    <div><div className="text-[10px] text-slate-400 font-semibold">TRANSAKSI</div><div className="text-slate-500">{fmtDate(r.created_at)}</div></div>
                    <div><div className="text-[10px] text-slate-400 font-semibold">DIBATALKAN</div><div className="text-rose-600">{r.cancelled_at ? fmtDate(r.cancelled_at) : "-"}</div></div>
                    <div className="ml-auto text-right"><div className="text-[10px] text-slate-400 font-semibold">NILAI</div><div className="font-bold text-slate-800">{rupiah(r.total)}</div></div>
                  </div>
                  <div className="flex items-start gap-2 bg-slate-50 rounded-lg p-2 mt-2"><MessageSquare size={12} className="text-slate-400 mt-0.5 shrink-0" /><span className={`text-xs text-slate-500 ${open ? "" : "line-clamp-2"}`}>{r.reason || "Tanpa alasan"}</span></div>
                  <div className="flex justify-between mt-2 text-xs text-slate-500"><span>Dibatalkan oleh <b className="text-slate-700">{r.cancelled_by_name}</b></span><span className="text-primary font-semibold">{open ? "Tutup detail" : `${r.items.length} item · detail`}</span></div>
                  {open && (
                    <div className="border-t border-slate-100 mt-2 pt-2">
                      {r.items.map((it, i) => (
                        <div key={i} className="flex justify-between py-1 text-sm"><div className="min-w-0"><div className="font-medium text-slate-700">{num(it.qty)}× {it.name}</div>{it.variant_name && <div className="text-xs text-slate-400">Varian: {it.variant_name}</div>}{it.note && <div className="text-xs text-slate-400">{it.note}</div>}</div><span className="text-slate-500">{rupiah(it.subtotal)}</span></div>
                      ))}
                      {r.paid_amount ? <div className="flex justify-between text-sm mt-1 text-slate-500"><span>Sudah dibayar (dikembalikan)</span><span>{rupiah(r.paid_amount)}</span></div> : null}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

const Info = ({ label, value }) => (<div><div className="text-xs text-slate-400">{label}</div><div className="text-slate-800 font-bold mt-0.5">{value}</div></div>);
